import {useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';

import {ROUTE} from 'core/routes';
import {isAdmin$} from 'models/auth';
import {
    filtersChanged as listFiltersChanged,
    filtersReset as listFiltersReset,
    filtersSubmit as listFiltersSubmit,
    listState$,
} from 'models/registries/gtinsGrid/list';
import {
    filtersChanged as tableFiltersChanged,
    filtersReset as tableFiltersReset,
    tableState$,
} from 'models/registries/gtinsGrid/table';
import {GtinsModeFilter} from 'types/gtins';

const MODE_FILTER_KEY = 'modeFilter';

export const useGtinsModeFilter = ({isMobile}: {isMobile: boolean}) => {
    const {t} = useTranslation();
    const location = useLocation();
    const isAdmin = !!useStore(isAdmin$);
    const {showConfigBlock, filters: listFilters} = useStore(listState$);
    const {filters: tableFilters} = useStore(tableState$);

    const isFavoritesRoute = location.pathname === ROUTE.REGISTRIES_GTIN_FAVORITES;
    const isMyCreatedProductsRoute = location.pathname === ROUTE.REGISTRIES_GTIN_MY_CREATED;
    const showModeFilters = isFavoritesRoute || (isMyCreatedProductsRoute && isAdmin);
    const activeModeFilter = (isMobile ? listFilters : tableFilters)?.[MODE_FILTER_KEY] as
        | GtinsModeFilter
        | undefined;

    const setModeFilter = (modeFilter?: GtinsModeFilter) => {
        if (isMobile) {
            if (modeFilter) {
                listFiltersChanged([{id: MODE_FILTER_KEY, value: modeFilter}]);
                listFiltersSubmit();
                return;
            }

            listFiltersReset([MODE_FILTER_KEY]);
            return;
        }

        if (modeFilter) {
            tableFiltersChanged([{id: MODE_FILTER_KEY, value: modeFilter}]);
            return;
        }

        tableFiltersReset([MODE_FILTER_KEY]);
    };

    const modeFilterOptions = isFavoritesRoute
        ? [
              {
                  label: t('pages.registries.gtins.quickFilters.usedByOrders'),
                  value: 'USED_BY_ORDERS' as GtinsModeFilter,
              },
              {
                  label: t('pages.registries.gtins.quickFilters.usedByInvoices'),
                  value: 'USED_BY_INVOICES' as GtinsModeFilter,
              },
          ]
        : isMyCreatedProductsRoute && isAdmin
        ? [
              {
                  label: t('pages.registries.gtins.quickFilters.onlyLatestVersions'),
                  value: 'CURRENT' as GtinsModeFilter,
              },
              {
                  label: t('pages.registries.gtins.quickFilters.allVersions'),
                  value: undefined,
              },
          ]
        : [];

    return {
        activeModeFilter,
        modeFilterOptions,
        setModeFilter,
        showConfigBlock,
        showModeFilters,
    };
};
